import { toastError } from 'admination-react'
import { useEffect, useRef, useState } from 'react'
import { captureWriteIdentity, isWriteIdentityCurrent } from '@/core/api/http'
import { getVerbisLebenslaufStatus, isOhneDatum, type LebenslaufEintrag } from '../../api/verbisLebenslauf'
import { verbisLebenslaufStatusFailureMessage } from './errors'

export type VerbisLebenslaufDone = { verbis: boolean; lebenslauf: boolean }

export type LebenslaufOhneDatum = { position: number; eintrag: LebenslaufEintrag }

function ohneDatumAus(eintraege: readonly LebenslaufEintrag[], positionen: readonly number[]): LebenslaufOhneDatum[] {
  return positionen.map((position) => ({ position, eintrag: eintraege[position] }))
}

const NOT_DONE: VerbisLebenslaufDone = { verbis: false, lebenslauf: false }

export function useVerbisLebenslaufStatus(coachingId: string | null): {
  done: VerbisLebenslaufDone
  loading: boolean
  statusError: boolean
  markDone: (step: 'verbis' | 'lebenslauf') => void
  reload: () => void
  ohneDatum: readonly LebenslaufOhneDatum[]
  korrigiert: (position: number, eintrag: LebenslaufEintrag, ohneDatum: readonly number[]) => void
} {
  const [done, setDone] = useState<VerbisLebenslaufDone>(NOT_DONE)
  const [loading, setLoading] = useState(true)
  const [statusError, setStatusError] = useState(false)
  const [version, setVersion] = useState(0)
  const [eintraege, setEintraege] = useState<readonly LebenslaufEintrag[]>([])
  const [ohneDatumPositionen, setOhneDatumPositionen] = useState<readonly number[]>([])
  const gelesenFuerRef = useRef<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const identity = captureWriteIdentity()
    if (gelesenFuerRef.current !== coachingId) {
      gelesenFuerRef.current = coachingId
      setDone(NOT_DONE)
      setEintraege([])
      setOhneDatumPositionen([])
      setLoading(!!coachingId)
    }
    setStatusError(false)
    if (!coachingId) return
    getVerbisLebenslaufStatus(coachingId)
      .then((status) => {
        if (cancelled || !isWriteIdentityCurrent(identity)) return
        setDone({ verbis: status.verbis !== null, lebenslauf: status.lebenslauf !== null })
        setEintraege(status.lebenslauf?.eintraege ?? [])
        setOhneDatumPositionen(status.lebenslauf?.ohne_datum ?? [])
      })
      .catch((e) => {
        if (cancelled || !isWriteIdentityCurrent(identity)) return
        console.error('VerBIS/Lebenslauf-Status laden fehlgeschlagen:', e)
        toastError(verbisLebenslaufStatusFailureMessage(e))
        setStatusError(true)
      })
      .finally(() => {
        if (!cancelled && isWriteIdentityCurrent(identity)) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [coachingId, version])

  const markDone = (step: 'verbis' | 'lebenslauf') => setDone((prev) => ({ ...prev, [step]: true }))

  const reload = () => setVersion((prev) => prev + 1)

  const korrigiert = (position: number, eintrag: LebenslaufEintrag, ohneDatum: readonly number[]) => {
    if (!isOhneDatum(ohneDatum, eintraege)) throw new Error('Die Antwort des Servers hatte nicht die erwartete Form.')
    setEintraege((prev) => prev.map((alt, i) => (i === position ? eintrag : alt)))
    setOhneDatumPositionen(ohneDatum)
  }

  const ohneDatum = ohneDatumAus(eintraege, ohneDatumPositionen)

  return { done, loading, statusError, markDone, reload, ohneDatum, korrigiert }
}
